import { Client } from '@hey-api/client-fetch';

/**
 * Attaches a static Bearer token to the Authorization header of all requests made by the provided HTTP client.
 * Note: This style of authentication is not suitable for scenarios where the token may expire and needs to be
 * dynamically refreshed. In such cases, consider implementing a dynamic token
 * retrieval mechanism and authentication-on-demand
 *
 * @param client - The HTTP client to which the token will be attached.
 * @param token - The Bearer token to attach to the Authorization header.
 *
 * @remarks TODO: Ideally the process of refreshing this token should also be standardized.
 *            This will require a pattern for both micro-frontend applications and standard applications.
 */
export const attachStaticAuth = (client: Client, token: string): void => {
  client.interceptors.request.use((config: Request) => {
    config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  });
};

/**
 * Attaches an authenticated Bearer token to the Authorization header of all requests made by the provided HTTP client.
 * The token is retrieved dynamically from the provided function.
 *
 * @param client - The HTTP client to which the token will be attached.
 * @param token - The function to retrieve the Bearer token.
 */
export const attachAuth = (
  client: Client,
  token: () => Promise<string>
): void => {
  client.interceptors.request.use(async (config: Request) => {
    const accessToken = await token();
    config.headers.set('Authorization', `Bearer ${accessToken}`);
    return config;
  });
};

export const attachCustomerUid = (
  client: Client,
  customerUid: string
): void => {
  client.interceptors.request.use((config: Request) => {
    config.headers.set('x-visionlink-customeruid', customerUid);
    return config;
  });
};
